import { Mention } from "@tiptap/extension-mention";
import { PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer, ReactRenderer } from "@tiptap/react";

import { SkillChipView } from "./skill-chip-view";
import { SlashMenu, type SlashMenuItem, type SlashMenuRef } from "./slash-menu";

export type SkillMentionConfig = {
	getItems: (query: string) => SlashMenuItem[];
};

export const skillMentionPluginKey = new PluginKey("skillMention");

export function createSkillMention({ getItems }: SkillMentionConfig) {
	return Mention.extend({
		name: "skill",
		addAttributes() {
			return {
				id: { default: null },
				label: { default: null },
				description: {
					default: null,
					parseHTML: (el) => el.getAttribute("data-description"),
					renderHTML: (attrs) =>
						attrs.description
							? { "data-description": attrs.description as string }
							: {},
				},
			};
		},
		addNodeView() {
			return ReactNodeViewRenderer(SkillChipView, { as: "span" }) as never;
		},
	}).configure({
		deleteTriggerWithBackspace: true,
		renderText({ node }) {
			return `/${node.attrs.id}`;
		},
		suggestion: {
			char: "/",
			pluginKey: skillMentionPluginKey,
			allowSpaces: false,
			items: ({ query }) => getItems(query),
			command: ({ editor, range, props }) => {
				const item = props as unknown as SlashMenuItem;
				editor
					.chain()
					.focus()
					.insertContentAt(range, [
						{
							type: "skill",
							attrs: {
								id: item.slug,
								label: item.name,
								description: item.description ?? null,
							},
						},
						{ type: "text", text: " " },
					])
					.run();
			},
			render: () => {
				let renderer: ReactRenderer<SlashMenuRef> | null = null;

				return {
					onStart: (props) => {
						renderer = new ReactRenderer(SlashMenu, {
							props: {
								items: props.items as SlashMenuItem[],
								command: props.command,
								clientRect: props.clientRect ?? null,
							},
							editor: props.editor as never,
						});
					},
					onUpdate: (props) => {
						renderer?.updateProps({
							items: props.items as SlashMenuItem[],
							command: props.command,
							clientRect: props.clientRect ?? null,
						});
					},
					onKeyDown: (props) => {
						if (props.event.key === "Escape") {
							renderer?.destroy();
							renderer = null;
							return true;
						}
						return renderer?.ref?.onKeyDown(props.event) ?? false;
					},
					onExit: () => {
						renderer?.destroy();
						renderer = null;
					},
				};
			},
		},
	});
}
